/**
 * @file index.tsx
 *
 * @fileoverview Shows the job preferences widget when in view mode.
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Image } from '~/jobiqo/components/Image';
import { EditLink } from '~/jobiqo/components/Organization/Widgets/EditLink';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import styles from './infoBox.module.scss';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { OfficeIcon } from '~/vcl/icons/Office';
import { OrganicIcon } from '~/vcl/icons/Organic';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import OrganizationDetailHeaderExtraInfoSlot from '~/jobiqo/components/Slots/OrganizationDetailHeaderExtraInfoSlot';
import { OrganizationDetailNoLogoSlot } from '~/jobiqo/components/Slots/OrganizationDetailNoLogoSlot';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface OrganizationInfoBoxWidgetViewProps {
  /**
   * The current resume.
   */
  organization: OrganizationProfile;
  /**
   * The current box id.
   */
  boxId: string | undefined;
}

export const OrganizationInfoBoxWidgetView = ({
  organization,
  boxId,
}: OrganizationInfoBoxWidgetViewProps) => {
  const { ORGANIZATIONS } = useFrontendConfig();
  const ORGANIZATION_PROFILE_INFO_BOX_FIELDS =
    ORGANIZATIONS.ORGANIZATION_PROFILE_INFO_BOX_FIELDS;
  const organizationAddress = organization.organizationLocation?.address
    ?.replace(/\s*[\r\n]+\s*/g, ' ')
    .trim();

  return (
    <div>
      <FlexItems justifyContent="flex-end">
        <EditLink organization={organization} boxId={boxId} />
      </FlexItems>
      <div className={styles.responsiveGrid}>
        <Cell alignItems="center" className={styles.logo}>
          {organization.logo?.url ? (
            <Image
              width={100}
              height={100}
              src={organization.logo?.url}
              alt={
                organization.logo?.alt
                  ? organization.logo?.alt
                  : `${organization.name} logo`
              }
              {...(organization.logo?.title && {
                title: organization.logo?.title,
              })}
            />
          ) : (
            <OrganizationDetailNoLogoSlot organization={organization} />
          )}
        </Cell>

        <Cell>
          <Text tag="h1" lineHeight={4} fontSize={5}>
            {organization.name}
          </Text>
          <Spacing />
          {ORGANIZATION_PROFILE_INFO_BOX_FIELDS.organizationLocation &&
            organizationAddress && (
              <>
                <FlexItems>
                  <FlexItem marginRight={5}>
                    <OfficeIcon title="Office" color="primary" />
                  </FlexItem>
                  <FlexItem marginRight={5}>
                    <Text margin={0} color="gray4">
                      <FormattedMessage
                        id="organization_location_label"
                        defaultMessage="Headquarters"
                      />
                    </Text>
                  </FlexItem>
                  <FlexItem>
                    <Text margin={0} color="gray8">
                      {organizationAddress}
                    </Text>
                  </FlexItem>
                </FlexItems>
                <Spacing />
              </>
            )}
          {ORGANIZATION_PROFILE_INFO_BOX_FIELDS.industry &&
            organization.industry && (
              <>
                <FlexItems>
                  <FlexItem marginRight={5}>
                    <OrganicIcon title="Office" color="primary" />
                  </FlexItem>
                  <FlexItem marginRight={5}>
                    <Text margin={0} color="gray4">
                      <FormattedMessage
                        id="organization_industry_label"
                        defaultMessage="Industry"
                      />
                    </Text>
                  </FlexItem>
                  <FlexItem>
                    <Text margin={0} color="gray8">
                      {organization.industry.label}
                    </Text>
                  </FlexItem>
                </FlexItems>
                <Spacing />
              </>
            )}
          {/* Because we don't have always highlightLink enabled we have to override the type which might not have this field. */}
          {(organization as any)?.highlightLink &&
            (organization as any)?.highlightLinkText && (
              <>
                <a
                  target="_blank"
                  href={(organization as any)?.highlightLink}
                  rel="noreferrer"
                >
                  <Button buttonStyle="primary">
                    <span>{(organization as any)?.highlightLinkText}</span>
                  </Button>
                </a>
              </>
            )}
          <OrganizationDetailHeaderExtraInfoSlot organization={organization} />
        </Cell>
      </div>
    </div>
  );
};
export default OrganizationInfoBoxWidgetView;
